import { BarChart, Bar, YAxis, ReferenceLine } from "recharts";
import {
  formatCalendarWeekdayDateOrDash,
  formatCurrencyGbp,
} from "@shared-core/domain/formatters";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "../../primitives/chart";

// Daily quoted £ over the last 30 days, one bar per calendar day (weekends read
// ~0). Minimal furniture — no axes; the dashed pace-needed line is keyed by the
// legend the card renders above the chart. `paceGbp` is null when no pace can be
// derived (no target / no margin / plateau 0) — then no line is drawn. Forecast-
// domain pink; house ChartContainer for the styled tooltip.
const NEUTRAL = "#a3a3a3";

export interface QuoteVolumePoint {
  date: string; // "YYYY-MM-DD" calendar date, no time-of-day
  amt: number;
}

const chartConfig = {
  v: { label: "Quoted", color: "#f472b6" },
} satisfies ChartConfig;

function dayLabel(date: string): string {
  return formatCalendarWeekdayDateOrDash(date);
}

export function QuoteVolumeChart({
  points,
  paceGbp,
}: {
  points: QuoteVolumePoint[];
  paceGbp: number | null;
}) {
  const data = points.map((p, i) => ({
    i,
    v: p.amt,
    label:
      i === points.length - 1
        ? `${dayLabel(p.date)} (today)`
        : dayLabel(p.date),
  }));
  const niceMax = Math.max(...points.map((p) => p.amt), paceGbp ?? 0) * 1.1;

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-20 w-full">
      <BarChart
        data={data}
        margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
        barCategoryGap={2}
      >
        <YAxis hide domain={[0, niceMax]} />
        <ChartTooltip
          cursor={{ fill: "#ececf0", opacity: 0.6 }}
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) =>
                payload?.[0]?.payload?.label ?? ""
              }
              formatter={(value, name, item) => (
                <div className="flex w-full items-center gap-2">
                  <div
                    className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                    style={{ background: item?.color ?? "#f472b6" }}
                  />
                  <span className="text-muted-foreground">
                    {chartConfig[name as keyof typeof chartConfig]?.label ??
                      name}
                  </span>
                  <span className="text-foreground ml-auto font-mono font-medium tabular-nums">
                    {formatCurrencyGbp(Number(value))}
                  </span>
                </div>
              )}
            />
          }
        />
        {paceGbp != null && (
          <ReferenceLine
            y={paceGbp}
            stroke={NEUTRAL}
            strokeWidth={2}
            strokeDasharray="6 5"
          />
        )}
        <Bar
          dataKey="v"
          fill="#f472b6"
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ChartContainer>
  );
}
